import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation,useRouteMatch } from 'react-router-dom'
import FeatherIcon from 'feather-icons-react'

import { ConfigContext } from '../../../contexts/ConfigContext'
import useWindowSize from '../../../hooks/useWindowSize'

import NavContent from './NavContent'
import defaultNavigation, {
  getNavigation,
} from '../../../utility/navigation.util'
import useAuth from 'hooks/useAuth'
import NavPerspective from './NavContent/NavPerspective'
import {TABLET_MIN_WIDTH} from 'config/constant'
import {
  addDentalServiceItemMenu,
  addLaboratoryItemMenu,
  addOperativeItemMenu,
} from './operative-item-menu.util'
import { addLabOrderFormDesignerMenu } from './lab-order-form-designer-menu.util'
import { addIcd9Menu } from './icd9-menu.util'
import { addIcd10Menu } from './icd10-menu.util'
import { addDeviceGatewayMenu } from './device-gateway-menu.util'
import { addHospitalLayoutMenu } from './hospital-layout-menu.util'

const menuLoadingRows = [1, 2, 3, 4, 5, 6, 7, 8, 9]
const routeNavigationPaths = [
  'patient',
  'users',
  'employee',
  'stock',
  'settings',
  'ecommerce',
  'menu',
]

const SidebarMenuLoading = ({ collapseMenu }) => {
  const rowHeight = collapseMenu ? 38 : 52

  return (
    <div className="navbar-content next-scroll">
      <div className="px-3 py-3">
        {!collapseMenu && (
          <div className="d-flex align-items-center mb-3 text-muted">
            <div className="spinner-border spinner-border-sm mr-2" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <span className="f-14">กำลังโหลดเมนู...</span>
          </div>
        )}
        {menuLoadingRows.map((row) => (
          <div
            key={`menu-loading-${row}`}
            className="d-flex align-items-center"
            style={{
              height: rowHeight,
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <div
              style={{
                width: collapseMenu ? 24 : 30,
                height: collapseMenu ? 24 : 30,
                borderRadius: 6,
                background: 'rgba(64, 153, 255, 0.14)',
              }}
            />
            {!collapseMenu && (
              <React.Fragment>
                <div className="w-100 ml-3">
                  <div
                    style={{
                      width: `${row % 3 === 0 ? 58 : row % 2 === 0 ? 72 : 84}%`,
                      height: 12,
                      borderRadius: 8,
                      background: 'rgba(64, 153, 255, 0.18)',
                    }}
                  />
                  <div
                    className="mt-2"
                    style={{
                      width: `${row % 2 === 0 ? 36 : 48}%`,
                      height: 8,
                      borderRadius: 8,
                      background: 'rgba(0, 0, 0, 0.07)',
                    }}
                  />
                </div>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: 'rgba(64, 153, 255, 0.18)',
                  }}
                />
              </React.Fragment>
            )}
          </div>
        ))}
        {!collapseMenu && (
          <div className="text-muted f-12 mt-3">
            กำลังตรวจสอบสิทธิ์และเมนูการใช้งาน
          </div>
        )}
      </div>
    </div>
  )
}

const SidebarMenuError = ({ collapseMenu, onRetry }) => {
  return (
    <div className="navbar-content next-scroll">
      <div className="px-3 py-4 text-center">
        <div className="text-muted mb-3">
          <FeatherIcon icon="alert-circle" size={collapseMenu ? 22 : 30} />
        </div>
        {!collapseMenu && (
          <React.Fragment>
            <div className="f-16 m-b-5">โหลดเมนูไม่สำเร็จ</div>
            <div className="text-muted f-13 mb-3">
              กรุณาลองโหลดเมนูอีกครั้ง
            </div>
          </React.Fragment>
        )}
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          title="ลองโหลดเมนูใหม่"
          onClick={onRetry}
        >
          <FeatherIcon icon="refresh-cw" size={14} className="mr-1" />
          {!collapseMenu && 'ลองใหม่'}
        </button>
      </div>
    </div>
  )
}

const SidebarMenuEmpty = ({ collapseMenu }) => {
  return (
    <div className="navbar-content next-scroll">
      <div className="px-3 py-4 text-center text-muted">
        <FeatherIcon icon="folder" size={collapseMenu ? 22 : 30} />
        {!collapseMenu && (
          <React.Fragment>
            <div className="f-16 mt-3">ยังไม่มีเมนูที่ใช้งานได้</div>
            <div className="f-13 mt-1">ระบบไม่พบเมนูตามสิทธิ์ของผู้ใช้</div>
          </React.Fragment>
        )}
      </div>
    </div>
  )
}

const Navigation = () => {
  const configContext = useContext(ConfigContext)
  const {
    layout,
    layoutType,
    navFixedLayout,
    collapseMenu,
    rtlLayout,
    boxLayout,
    subLayout,
    headerFixedLayout,
  } = configContext.state
  const windowSize = useWindowSize()
  let location = useLocation()
  const [navigation, setNavigation] = useState(defaultNavigation)
  let patientRouteMatch = useRouteMatch(
    '/patient-file/emr/:module/:submodule/'
  )
  const [perspectiveMenu, setPerspectiveMenu] = useState([])
  const [perspectiveMenuStatus, setPerspectiveMenuStatus] = useState('loading')
  const [perspectiveMenuRetry, setPerspectiveMenuRetry] = useState(0)
  const [perspective, setPerspective] = useState(null)  
  const { isAdmin, perspective:authPerspective } = useAuth();
  const navigationWithServiceItems = useMemo(
    () =>
      addHospitalLayoutMenu(
        addDeviceGatewayMenu(
          addLabOrderFormDesignerMenu(
            addLaboratoryItemMenu(
              addIcd10Menu(
                addIcd9Menu(
                  addDentalServiceItemMenu(
                    addOperativeItemMenu(perspectiveMenu),
                  ),
                ),
              ),
            ),
          ),
          { isAdmin },
        ),
        { isAdmin },
      ),
    [perspectiveMenu, isAdmin],
  )
  useEffect(() => {
    const firstPath = location.pathname.split('/')[1]
    const routeNavigation = getNavigation(location)
    const hasRouteNavigation = routeNavigationPaths.includes(firstPath)

    if (hasRouteNavigation) {
      setNavigation(routeNavigation)
      return
    }

    if(authPerspective){
      setNavigation({items:authPerspective.map((item)=>{
        return {
          id: item.perspectiveId,
          title: item.name,
          type: 'group',
          icon: 'fa fa-spinner',
          color: '#4099ff',
          iconType:'char',
          backIcon :'fa fa-angle-double-right',
          routePath : item.routePath
      }
      })})
      return
    }

    setNavigation(routeNavigation)
  }, [location,authPerspective])

  const scroll = () => {
    if (navFixedLayout && headerFixedLayout === false) {
      const main = document.querySelector('.pcoded-navbar')
      const el = document.querySelector('.pcoded-navbar.menupos-fixed')
      if (!main || !el) return
      const scrollPosition = window.pageYOffset
      if (scrollPosition > 60) {
        el.style.position = 'fixed'
        el.style.transition = 'none'
        el.style.marginTop = '0'
    
      } else {
        main.style.position = 'absolute'
        main.style.marginTop = '56px'
      }
    } else {
      document.querySelector('.pcoded-navbar')?.removeAttribute('style')
    }
  }  

  useEffect(() => {
    if (layout === 'horizontal') return
    if (!(navFixedLayout && !headerFixedLayout)) return

    window.addEventListener('scroll', scroll, true)
    scroll()

    return () => {
      window.removeEventListener('scroll', scroll, true)
    }
  }, [layout, navFixedLayout, headerFixedLayout])

  let navClass = [`pcoded-navbar ${patientRouteMatch?'menu-sub-level':''}`, layoutType]

  if (layout === 'horizontal') {
    navClass = [...navClass, 'theme-horizontal']
  } else {
    if (navFixedLayout) {
      navClass = [...navClass, 'menupos-fixed']
    }
  }

  //FIXED ME : Removed temporary to test ipad-PRO , it remove collapsed
  if (windowSize.width < TABLET_MIN_WIDTH && collapseMenu) {
    
    navClass = [...navClass, 'mob-open']
  }else if (collapseMenu) {
    navClass = [...navClass, 'navbar-collapsed']
  }

  if (layoutType === 'dark') {
    document.body.classList.add('gradient-able-dark')
  } else {
    document.body.classList.remove('gradient-able-dark')
  }

  if (rtlLayout) {
    document.body.classList.add('gradient-able-rtl')
  } else {
    document.body.classList.remove('gradient-able-rtl')
  }

  if (boxLayout) {
    document.body.classList.add('container')
    document.body.classList.add('box-layout')
  } else {
    document.body.classList.remove('container')
    document.body.classList.remove('box-layout')
  }

  let navBarClass = ['navbar-wrapper']
  if (layout === 'horizontal' && subLayout === 'horizontal-2') {
    navBarClass = [...navBarClass, 'container']
  }

  const renderNavContent = () => {
    const firstPath = location.pathname.split('/')[1]
    const hasRouteNavigation = routeNavigationPaths.includes(firstPath)
    const perspectiveRoutePath = perspective?.routePath || ''
    const isPerspectiveChanging =
      perspectiveRoutePath &&
      perspectiveRoutePath !== '/' &&
      location.pathname !== perspectiveRoutePath &&
      !location.pathname.startsWith(`${perspectiveRoutePath}/`)

    if (hasRouteNavigation && !perspective?.perspectiveId) {
      return <NavContent navigation={navigation.items} />
    }

    if (perspectiveMenuStatus === 'loading' || isPerspectiveChanging) {
      return <SidebarMenuLoading collapseMenu={collapseMenu} />
    }

    if (perspectiveMenuStatus === 'error') {
      return (
        <SidebarMenuError
          collapseMenu={collapseMenu}
          onRetry={() => setPerspectiveMenuRetry((value) => value + 1)}
        />
      )
    }

    if (!hasRouteNavigation && firstPath && !perspective?.perspectiveId) {
      return <SidebarMenuEmpty collapseMenu={collapseMenu} />
    }

    if (perspective?.perspectiveId) {
      if (perspectiveMenuStatus === 'ready' && perspectiveMenu.length === 0) {
        return <SidebarMenuEmpty collapseMenu={collapseMenu} />
      }
      return <NavContent navigation={navigationWithServiceItems || []} />
    }

    return <NavContent navigation={navigation.items} />
  }

  let navContent = (
    <div className={navBarClass.join(' ')}>
      <NavPerspective
        outside={false}
        collapseMenu={collapseMenu}
        setPerspectiveMenu={setPerspectiveMenu}
        setPerspectiveMenuStatus={setPerspectiveMenuStatus}
        menuRetryToken={perspectiveMenuRetry}
        perspective={perspective}
        setPerspective={setPerspective}
      />
      <hr className="mb-0 mt-0" />
      {renderNavContent()}
    </div>
  )

  if (windowSize.width < TABLET_MIN_WIDTH) {
    navContent = (
      <div className="navbar-wrapper">
        <NavPerspective
          outside={true}
          collapseMenu={collapseMenu}
          setPerspectiveMenu={setPerspectiveMenu}
          setPerspectiveMenuStatus={setPerspectiveMenuStatus}
          menuRetryToken={perspectiveMenuRetry}
          perspective={perspective}
          setPerspective={setPerspective}
        />
        <hr className="mb-0 mt-0" />
        {renderNavContent()}
      </div>
    )
  }

  return (
    <React.Fragment>
      <nav className={navClass.join(' ')}>{navContent}</nav>
    </React.Fragment>
  )
}

export default Navigation
